// LinkedIn Integration Service for APN
// Handles LinkedIn groups, graduate sourcing, and professional networking

// LinkedIn API configuration
const LINKEDIN_CONFIG = {
  clientId: process.env.REACT_APP_LINKEDIN_CLIENT_ID || 'demo-client-id',
  redirectUri: `${window.location.origin}/auth/linkedin/callback`,
  scope: 'r_liteprofile r_emailaddress w_member_social',
  apiBaseUrl: 'https://api.linkedin.com/v2'
};

// Check if LinkedIn is properly configured
const isLinkedInConfigured = () => {
  return process.env.REACT_APP_LINKEDIN_CLIENT_ID &&
         process.env.REACT_APP_LINKEDIN_CLIENT_ID !== 'your-linkedin-client-id';
};

// APN LinkedIn Groups for networking and sourcing
export const APN_LINKEDIN_GROUPS = {
  'finance-professionals': {
    id: 'finance-professionals-network',
    name: 'Finance Professionals Network',
    description: 'Connect with finance professionals across all industries',
    category: 'Professional',
    memberCount: 15000,
    linkedinUrl: 'https://www.linkedin.com/groups/finance-professionals-network',
    isActive: true
  },
  'recent-graduates': {
    id: 'finance-recent-graduates',
    name: 'Finance Recent Graduates',
    description: 'New graduates in finance seeking opportunities and mentorship',
    category: 'Graduate',
    memberCount: 8500,
    linkedinUrl: 'https://www.linkedin.com/groups/finance-recent-graduates',
    isActive: true
  },
  'investment-banking': {
    id: 'investment-banking-professionals',
    name: 'Investment Banking Professionals',
    description: 'Investment banking analysts, associates, and VPs',
    category: 'Specialty',
    memberCount: 12000,
    linkedinUrl: 'https://www.linkedin.com/groups/investment-banking-professionals',
    isActive: true
  },
  'corporate-finance': {
    id: 'corporate-finance-network',
    name: 'Corporate Finance Network',
    description: 'Corporate finance professionals and FP&A specialists',
    category: 'Specialty',
    memberCount: 9800,
    linkedinUrl: 'https://www.linkedin.com/groups/corporate-finance-network',
    isActive: true
  },
  'accounting-professionals': {
    id: 'accounting-professionals-network',
    name: 'Accounting Professionals Network',
    description: 'CPAs, accounting managers, and finance professionals',
    category: 'Professional',
    memberCount: 18500,
    linkedinUrl: 'https://www.linkedin.com/groups/accounting-professionals-network',
    isActive: true
  }
};

// Graduate sourcing criteria
export const GRADUATE_SOURCING_CRITERIA = {
  education: {
    degrees: ['Bachelor', 'Master', 'MBA'],
    majors: [
      'Finance',
      'Accounting',
      'Economics',
      'Business Administration',
      'Mathematics',
      'Statistics',
      'Data Science'
    ],
    graduationYears: [2023, 2024, 2025] // Recent graduates
  },
  experience: {
    maxYears: 2,
    relevantInternships: [
      'Investment Banking',
      'Corporate Finance',
      'Accounting',
      'Financial Analysis',
      'Consulting'
    ]
  },
  skills: [
    'Financial Modeling',
    'Excel',
    'PowerPoint',
    'SQL',
    'Python',
    'R',
    'Tableau',
    'Financial Analysis',
    'Valuation'
  ]
};

// Initialize LinkedIn SDK
export const initializeLinkedInSDK = () => {
  return new Promise((resolve, reject) => {
    if (!isLinkedInConfigured()) {
      reject(new Error('LinkedIn integration is not configured. Please contact your administrator.'));
      return;
    }

    if (window.IN) {
      resolve(window.IN);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://platform.linkedin.com/in.js';
    script.async = true;
    script.onload = () => {
      window.IN.init({
        api_key: LINKEDIN_CONFIG.clientId,
        authorize: true,
        onLoad: 'onLinkedInLoad'
      });
      resolve(window.IN);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

// LinkedIn OAuth login
export const linkedInLogin = async () => {
  try {
    await initializeLinkedInSDK();
    
    return new Promise((resolve, reject) => {
      window.IN.User.authorize((response) => {
        if (response.error) {
          reject(new Error(response.error_description || 'LinkedIn login failed'));
        } else {
          resolve(response);
        }
      });
    });
  } catch (error) {
    console.error('LinkedIn login error:', error);
    throw error;
  }
};

// Get LinkedIn profile data
export const getLinkedInProfile = async () => {
  try {
    await initializeLinkedInSDK();
    
    return new Promise((resolve, reject) => {
      window.IN.API.Profile('me')
        .fields(['id', 'firstName', 'lastName', 'emailAddress', 'headline', 'industry', 'location', 'positions'])
        .result((profile) => {
          resolve(profile.values[0]);
        })
        .error(reject);
    });
  } catch (error) {
    console.error('Error fetching LinkedIn profile:', error);
    throw error;
  }
};

// Search for recent graduates in LinkedIn groups
export const searchRecentGraduates = async (criteria = {}) => {
  try {
    // This would integrate with LinkedIn's API to search for recent graduates
    // For now, we'll return mock data that represents the structure
    
    const searchCriteria = {
      ...GRADUATE_SOURCING_CRITERIA,
      ...criteria
    };

    // Mock recent graduates data
    const mockGraduates = [
      {
        id: 'grad-001',
        name: 'Sarah Johnson',
        degree: 'Bachelor of Finance',
        university: 'University of Pennsylvania',
        graduationYear: 2024,
        location: 'New York, NY',
        skills: ['Financial Modeling', 'Excel', 'PowerPoint'],
        internships: ['Goldman Sachs - Investment Banking Intern'],
        linkedinUrl: 'https://linkedin.com/in/sarah-johnson-finance',
        isAvailable: true,
        preferredRoles: ['Investment Banking Analyst', 'Corporate Finance Analyst']
      },
      {
        id: 'grad-002',
        name: 'Michael Chen',
        degree: 'Master of Business Administration',
        university: 'Northwestern Kellogg',
        graduationYear: 2024,
        location: 'Chicago, IL',
        skills: ['Financial Analysis', 'SQL', 'Python', 'Valuation'],
        internships: ['McKinsey & Company - Business Analyst Intern'],
        linkedinUrl: 'https://linkedin.com/in/michael-chen-mba',
        isAvailable: true,
        preferredRoles: ['Corporate Development', 'Strategy & Operations']
      },
      {
        id: 'grad-003',
        name: 'Emily Rodriguez',
        degree: 'Bachelor of Accounting',
        university: 'University of Texas at Austin',
        graduationYear: 2024,
        location: 'Austin, TX',
        skills: ['Financial Reporting', 'Excel', 'QuickBooks', 'Tableau'],
        internships: ['PwC - Audit Intern', 'Dell - Finance Intern'],
        linkedinUrl: 'https://linkedin.com/in/emily-rodriguez-cpa',
        isAvailable: true,
        preferredRoles: ['Staff Accountant', 'Financial Analyst']
      }
    ];

    // Filter based on criteria
    const filteredGraduates = mockGraduates.filter(grad => {
      if (criteria.location && !grad.location.toLowerCase().includes(criteria.location.toLowerCase())) {
        return false;
      }
      if (criteria.graduationYear && grad.graduationYear !== criteria.graduationYear) {
        return false;
      }
      if (criteria.skills && !criteria.skills.some(skill => grad.skills.includes(skill))) {
        return false;
      }
      return true;
    });

    return {
      graduates: filteredGraduates,
      totalCount: filteredGraduates.length,
      searchCriteria
    };
  } catch (error) {
    console.error('Error searching recent graduates:', error);
    throw error;
  }
};

// Get LinkedIn group members (for sourcing)
export const getLinkedInGroupMembers = async (groupId, filters = {}) => {
  try {
    const group = APN_LINKEDIN_GROUPS[groupId];
    if (!group) {
      throw new Error('LinkedIn group not found');
    }

    // Mock group members data
    const mockMembers = [
      {
        id: 'member-001',
        name: 'David Kim',
        headline: 'Senior Financial Analyst at Microsoft',
        location: 'Seattle, WA',
        experience: '3 years',
        skills: ['Financial Planning', 'Budget Analysis', 'Excel'],
        linkedinUrl: 'https://linkedin.com/in/david-kim-finance',
        groupRole: 'Member',
        joinDate: '2023-01-15'
      },
      {
        id: 'member-002',
        name: 'Lisa Wang',
        headline: 'Investment Banking Associate at JPMorgan Chase',
        location: 'New York, NY',
        experience: '2 years',
        skills: ['M&A', 'Financial Modeling', 'Valuation'],
        linkedinUrl: 'https://linkedin.com/in/lisa-wang-ib',
        groupRole: 'Active Member',
        joinDate: '2022-08-20'
      }
    ];

    return {
      groupInfo: group,
      members: mockMembers,
      totalMembers: group.memberCount,
      filters
    };
  } catch (error) {
    console.error('Error fetching LinkedIn group members:', error);
    throw error;
  }
};

// Share job/project to LinkedIn groups
export const shareToLinkedInGroups = async (content, selectedGroups = []) => {
  try {
    await initializeLinkedInSDK();

    const sharePromises = selectedGroups.map(async (groupId) => {
      const group = APN_LINKEDIN_GROUPS[groupId];
      if (!group) return null;

      // This would use LinkedIn's API to share content to groups
      // For now, we'll simulate the sharing process

      return {
        groupId,
        groupName: group.name,
        shared: true,
        shareUrl: `${group.linkedinUrl}/share`,
        timestamp: new Date().toISOString()
      };
    });

    const results = await Promise.all(sharePromises);
    return results.filter(result => result !== null);
  } catch (error) {
    console.error('Error sharing to LinkedIn groups:', error);
    throw error;
  }
};

// Track referrals from LinkedIn
export const trackLinkedInReferral = async (referralData) => {
  try {
    const referral = {
      ...referralData,
      source: 'linkedin',
      timestamp: new Date().toISOString(),
      status: 'pending'
    };

    // In a real implementation, this would save to Firestore
    console.log('LinkedIn referral tracked:', referral);

    return referral;
  } catch (error) {
    console.error('Error tracking LinkedIn referral:', error);
    throw error;
  }
};

// Enhanced referral tracking with tiered discounts
export const processReferralRewards = async (referrerUserId, referralCount) => {
  try {
    let discountPercentage = 5; // Base 5% for all members
    let badgeEarned = null;

    // Tiered referral system
    if (referralCount >= 10) {
      discountPercentage = 5; // 5% for subsequent referrals after 10
      if (referralCount === 10) {
        badgeEarned = 'ambassador'; // Ambassador badge at 10 referrals
      }
    } else {
      discountPercentage = 5; // 5% for first 10 referrals
    }

    const reward = {
      referrerUserId,
      referralCount,
      discountPercentage,
      badgeEarned,
      timestamp: new Date().toISOString()
    };

    console.log('Referral reward processed:', reward);
    return reward;
  } catch (error) {
    console.error('Error processing referral rewards:', error);
    throw error;
  }
};

export default {
  APN_LINKEDIN_GROUPS,
  GRADUATE_SOURCING_CRITERIA,
  initializeLinkedInSDK,
  linkedInLogin,
  getLinkedInProfile,
  searchRecentGraduates,
  getLinkedInGroupMembers,
  shareToLinkedInGroups,
  trackLinkedInReferral
};
